// ---------------------------------------------------------------------------
// The one place the browser talks to the API.
//
// What it handles for you:
//   * `credentials: 'include'` so the HTTP-only session cookies travel.
//   * the CSRF header on every write.
//   * turning an error response into a typed ApiError with a readable message.
//   * one automatic retry after silently refreshing an expired session, so a
//     cashier is never thrown back to the login screen mid-order just because
//     fifteen minutes elapsed.
// ---------------------------------------------------------------------------

export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_SUSPENDED'
  | 'ACCOUNT_LOCKED'
  | 'NOT_AUTHENTICATED'
  | 'SESSION_EXPIRED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  /// A living option of the same product already has this name.
  | 'OPTION_NAME_TAKEN'
  /// The shift cannot close while delivery accounts are still outside.
  | 'DELIVERY_ACCOUNTS_PENDING'
  | 'SHIFT_SYNC_PENDING'
  | 'RATE_LIMITED'
  | 'CSRF_FAILED'
  | 'SHIFT_NOT_OPEN'
  /// The shift is open but its business day has ended. A different answer
  /// from SHIFT_NOT_OPEN: not "open a shift" but "close yesterday's first".
  | 'SHIFT_DAY_ENDED'
  /// A category needs preparation and names no usable printer. A CONFIGURATION
  /// error, not a printer fault: retrying will not help, somebody has to open
  /// Settings. The till keeps the cart and says so.
  | 'PREPARATION_PRINTER_NOT_SET'
  | 'RECEIPT_PRINTER_NOT_SET'
  | 'PAYLOAD_TOO_LARGE'
  | 'INTERNAL_ERROR'
  | 'NETWORK_ERROR';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  /// Field-level messages, ready to attach to form inputs.
  readonly details?: Record<string, string[]>;
  readonly meta?: Record<string, unknown>;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    details?: Record<string, string[]>,
    meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.meta = meta;
  }

  /// True when retrying might actually help.
  get isRetryable(): boolean {
    return this.code === 'NETWORK_ERROR' || this.status >= 500;
  }
}

const BASE_URL = '/api';

/// Read the readable CSRF cookie the server set at sign-in.
function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)yaghi_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /// Set internally to stop a refresh loop.
  _isRetry?: boolean;
}

/// Called when the session cannot be recovered, so the app can show the login
/// screen. Registered by the auth store rather than imported, to avoid a
/// circular dependency between the client and the store.
let onSessionLost: (() => void) | null = null;
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

async function parseError(response: Response): Promise<ApiError> {
  let code: ApiErrorCode = 'INTERNAL_ERROR';
  let message = 'Something went wrong. Please try again.';
  let details: Record<string, string[]> | undefined;
  let meta: Record<string, unknown> | undefined;

  try {
    const body = (await response.json()) as {
      error?: { code?: ApiErrorCode; message?: string; details?: Record<string, string[]>; meta?: Record<string, unknown> };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
      meta = body.error.meta;
    }
  } catch {
    // A non-JSON error body (a proxy error page, for instance). Keep the
    // generic message rather than showing the user raw HTML.
    if (response.status === 404) message = 'That address does not exist on the server.';
    if (response.status === 502 || response.status === 503) {
      message = 'The server is not reachable right now. Please try again in a moment.';
    }
  }

  return new ApiError(response.status, code, message, details, meta);
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // `body` and `_isRetry` are ours, not fetch's. They are pulled out here so
  // the rest can be handed to fetch untouched - passing our `unknown` body
  // straight through would not satisfy fetch's RequestInit type.
  const { body, _isRetry: _ignored, headers: callerHeaders, ...fetchOptions } = options;

  const method = (options.method ?? 'GET').toUpperCase();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((callerHeaders as Record<string, string>) ?? {}),
  };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (WRITE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...fetchOptions,
      method,
      headers,
      // Without this the browser would not send the authentication cookies.
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    // fetch only rejects when the request never reached the server.
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      'The server could not be reached. Please check the connection and try again. Nothing was saved.',
    );
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  const error = await parseError(response);

  // The access token expired. Refresh once, then replay the original request.
  const canRetry =
    error.code === 'SESSION_EXPIRED' &&
    !options._isRetry &&
    !path.startsWith('/auth/refresh') &&
    !path.startsWith('/auth/login');

  if (canRetry) {
    try {
      await request('/auth/refresh', { method: 'POST', _isRetry: true });
      return await request<T>(path, { ...options, _isRetry: true });
    } catch (refreshError) {
      // WHY THIS IS NOT A BLANKET `catch`, and it is the whole reason a
      // cashier was being thrown out mid-shift.
      //
      // A refresh can fail two ways that mean opposite things. The server
      // REFUSING it - the session is expired, revoked, or the account is
      // disabled - genuinely ends the session. The request never ARRIVING -
      // the shop's Wi-Fi dropped for four seconds, the server restarted after
      // a deploy, a proxy timed out - means nothing about the session at all.
      //
      // Treating the second as the first signed out a cashier standing in
      // front of a customer, with a perfectly valid seven-day refresh token
      // in a cookie, because one request missed.
      const rejected =
        refreshError instanceof ApiError &&
        refreshError.code !== 'NETWORK_ERROR' &&
        refreshError.status !== 0 &&
        refreshError.status < 500;

      // NOT signalled again here. The nested refresh call went through this
      // same function, and a genuine rejection has already been reported by
      // the branch at the bottom - saying so twice would reset the auth store
      // twice for one event.
      //
      // What this DOES decide is which error the caller sees. A transport
      // failure is reported as itself, so the caller can tell "try again in a
      // moment" from "you are signed out", which is the whole distinction.
      throw !rejected && refreshError instanceof ApiError ? refreshError : error;
    }
  }

  if (error.code === 'SESSION_EXPIRED' || error.code === 'NOT_AUTHENTICATED') {
    onSessionLost?.();
  }

  throw error;
}

/// Send a file, as multipart form data.
///
/// Deliberately separate from `request`: multipart must NOT carry a
/// Content-Type header of our own. The browser has to set it itself, because
/// only the browser knows the boundary string it generated. Setting
/// `Content-Type: multipart/form-data` by hand produces a body the server
/// cannot parse, and the failure looks like an empty upload.
async function sendForm<T>(path: string, form: FormData): Promise<T> {
  const csrf = readCsrfToken();

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', ...(csrf ? { 'x-csrf-token': csrf } : {}) },
      credentials: 'include',
      body: form,
    });
  } catch {
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      'The server could not be reached. Please check the connection and try again. Nothing was saved.',
    );
  }

  if (response.ok) return (await response.json()) as T;

  const error = await parseError(response);
  if (error.code === 'SESSION_EXPIRED' || error.code === 'NOT_AUTHENTICATED') onSessionLost?.();
  throw error;
}

/// Fetch a file and hand it to the browser's downloads.
///
/// The whole body is read into memory first rather than pointing a link at the
/// address. That is not laziness: the download must carry the session cookie
/// and be able to surface a permission refusal as a readable sentence. A plain
/// `<a href>` would send the request outside fetch, and a 403 would arrive as
/// a browser error page - or worse, as a file called `sales.csv` containing
/// JSON that says "forbidden".
async function download(path: string, fallbackName: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'GET',
      headers: { Accept: '*/*' },
      credentials: 'include',
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached, so nothing was downloaded.');
  }

  if (!response.ok) {
    const error = await parseError(response);
    if (error.code === 'SESSION_EXPIRED' || error.code === 'NOT_AUTHENTICATED') onSessionLost?.();
    throw error;
  }

  // The server names the file; the fallback is only for a proxy that strips
  // the header.
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const fileName = match?.[1] ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Freed on the next tick; revoking immediately can cancel the download in
  // some browsers before it has started reading.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  postForm: sendForm,
  download,
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};
